import { useState, useCallback } from 'react'
import { ReservationTargetType } from '../../../graphql'

export type TransferOrderLineItem = {
	id: string,
	productId: string,
	quantityRequested: number,
	allocationTargetType: ReservationTargetType | '',
	allocationTargetId: string,
	lineNote: string,
	demandRequestLineItemId?: string,
	selectedPart?: any,
}

export type TransferOrderFormData = {
	fromStoreId: string,
	toStoreId: string,
	requesterNote: string,
	lineItems: TransferOrderLineItem[],
}

export type TransferOrderFormValidation = {
	fromStoreId: {
		isInvalid: boolean,
		error?: string,
	},
	toStoreId: {
		isInvalid: boolean,
		error?: string,
	},
	lineItems: {
		isInvalid: boolean,
		error?: string,
	},
}

const getEmptyLineItem = (): TransferOrderLineItem => ( {
	id: `line-item-${Date.now()}-${Math.random().toString( 36 ).substr( 2, 9 )}`,
	productId: '',
	quantityRequested: 0,
	allocationTargetType: '',
	allocationTargetId: '',
	lineNote: '',
} )

export type TransferOrderInitialValues = {
	fromStoreId?: string,
	toStoreId?: string,
	requesterNote?: string,
	lineItems?: TransferOrderLineItem[],
}

const getInitialFormData = ( initialValues?: TransferOrderInitialValues ): TransferOrderFormData => ( {
	fromStoreId: initialValues?.fromStoreId ?? '',
	toStoreId: initialValues?.toStoreId ?? '',
	requesterNote: initialValues?.requesterNote ?? '',
	lineItems: initialValues?.lineItems?.length ? initialValues.lineItems : [ getEmptyLineItem() ],
} )

const getInitialValidation = (): TransferOrderFormValidation => ( {
	fromStoreId: { isInvalid: false },
	toStoreId: { isInvalid: false },
	lineItems: { isInvalid: false },
} )

export type UseTransferOrderFormReturn = {
	formData: TransferOrderFormData,
	validation: TransferOrderFormValidation,
	isValid: boolean,
	totalQuantity: number,
	lineItems: TransferOrderLineItem[],
	setFromStoreId: ( value: string ) => void,
	setToStoreId: ( value: string ) => void,
	setRequesterNote: ( value: string ) => void,
	addLineItem: () => void,
	removeLineItem: ( index: number ) => void,
	updateLineItem: ( index: number, updates: Partial<TransferOrderLineItem> ) => void,
	validateForm: () => boolean,
	resetForm: () => void,
}

export default function useTransferOrderForm( initialValues?: TransferOrderInitialValues ): UseTransferOrderFormReturn {
	const [ formData, setFormData ] = useState<TransferOrderFormData>( () => getInitialFormData( initialValues ) )
	const [ validation, setValidation ] = useState<TransferOrderFormValidation>( getInitialValidation )

	const validateLineItems = ( lineItems: TransferOrderLineItem[] ): { isInvalid: boolean, error?: string } => {
		if ( lineItems.length === 0 ) {
			return { isInvalid: true, error: 'At least one line item is required' }
		}

		for ( const item of lineItems ) {
			if ( !item.productId ) {
				return { isInvalid: true, error: 'All line items must have a product selected' }
			}
			if ( !item.quantityRequested || item.quantityRequested < 1 ) {
				return { isInvalid: true, error: 'All line items must have a valid quantity (minimum 1)' }
			}
			if ( item.allocationTargetType && !item.allocationTargetId ) {
				return { isInvalid: true, error: 'All line items with an allocation type must have a target selected' }
			}
		}

		return { isInvalid: false }
	}

	const validateForm = useCallback( (): boolean => {
		const newValidation: TransferOrderFormValidation = {
			fromStoreId: {
				isInvalid: !formData.fromStoreId,
				error: !formData.fromStoreId ? 'From store is required' : undefined,
			},
			toStoreId: {
				isInvalid: !formData.toStoreId || formData.toStoreId === formData.fromStoreId,
				error: !formData.toStoreId
					? 'To store is required'
					: formData.toStoreId === formData.fromStoreId
						? 'From store and To store must be different'
						: undefined,
			},
			lineItems: validateLineItems( formData.lineItems ),
		}

		setValidation( newValidation )

		return !Object.values( newValidation ).some( ( field ) => field.isInvalid )
	}, [ formData ] )

	const setFromStoreId = useCallback( ( value: string ) => {
		setFormData( ( prev ) => ( { ...prev, fromStoreId: value } ) )
		setValidation( ( prev ) => ( {
			...prev,
			fromStoreId: {
				isInvalid: !value,
				error: !value ? 'From store is required' : undefined,
			},
		} ) )
	}, [] )

	const setToStoreId = useCallback( ( value: string ) => {
		setFormData( ( prev ) => {
			const newData = { ...prev, toStoreId: value }
			setValidation( ( prevVal ) => ( {
				...prevVal,
				toStoreId: {
					isInvalid: !value || value === newData.fromStoreId,
					error: !value
						? 'To store is required'
						: value === newData.fromStoreId
							? 'From store and To store must be different'
							: undefined,
				},
			} ) )
			return newData
		} )
	}, [] )

	const setRequesterNote = useCallback( ( value: string ) => {
		setFormData( ( prev ) => ( { ...prev, requesterNote: value } ) )
	}, [] )

	const addLineItem = useCallback( () => {
		setFormData( ( prev ) => ( {
			...prev,
			lineItems: [ ...prev.lineItems, getEmptyLineItem() ],
		} ) )
		setValidation( ( prev ) => ( {
			...prev,
			lineItems: { isInvalid: false },
		} ) )
	}, [] )

	const removeLineItem = useCallback( ( index: number ) => {
		setFormData( ( prev ) => ( {
			...prev,
			lineItems: prev.lineItems.filter( ( _, i ) => i !== index ),
		} ) )
		setValidation( ( prev ) => ( {
			...prev,
			lineItems: { isInvalid: false },
		} ) )
	}, [] )

	const updateLineItem = useCallback( ( index: number, updates: Partial<TransferOrderLineItem> ) => {
		setFormData( ( prev ) => ( {
			...prev,
			lineItems: prev.lineItems.map( ( item, i ) =>
				( i === index ? { ...item, ...updates } : item ) ),
		} ) )
		setValidation( ( prev ) => ( {
			...prev,
			lineItems: { isInvalid: false },
		} ) )
	}, [] )

	const resetForm = useCallback( () => {
		setFormData( getInitialFormData() )
		setValidation( getInitialValidation() )
	}, [] )

	const isValid = !Object.values( validation ).some( ( field ) => field.isInvalid ) &&
		formData.fromStoreId !== '' &&
		formData.toStoreId !== '' &&
		formData.toStoreId !== formData.fromStoreId &&
		formData.lineItems.length > 0 &&
		formData.lineItems.every( ( item ) =>
			item.productId &&
			item.quantityRequested >= 1 &&
			( !item.allocationTargetType || !!item.allocationTargetId ) )

	const totalQuantity = formData.lineItems
		.reduce( ( sum, item ) => sum + ( item.quantityRequested || 0 ), 0 )

	return {
		formData,
		validation,
		isValid,
		totalQuantity,
		lineItems: formData.lineItems,
		setFromStoreId,
		setToStoreId,
		setRequesterNote,
		addLineItem,
		removeLineItem,
		updateLineItem,
		validateForm,
		resetForm,
	}
}
